import { Alert, Button, Empty, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { useMemo, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { ArrowRight, MapPin, Radio, Route } from 'lucide-react'
import { LookupBar } from '@/components/common/LookupBar'
import { MovementTable } from '@/components/journey/MovementTable'
import { PageContainer } from '@/components/common/PageContainer'
import { PageHeader } from '@/components/common/PageHeader'
import { SectionCard } from '@/components/common/SectionCard'
import { PageEmpty, PageError, PageLoading } from '@/components/common/PageStates'
import { dashboardService } from '@/services'
import { useRegionStore } from '@/stores/regionStore'
import { formatDateTime } from '@/utils/cn'
import type { LifecycleStage, MovementRow } from '@/types'

const { Text } = Typography

const CHART_COLORS = ['#af0000', '#1f2937', '#7c2d12', '#57534e', '#b91c1c', '#334155']

function partyName(code: string | null | undefined, name: string | null | undefined) {
  if (name?.trim()) return name.trim()
  if (code?.trim()) return code.trim()
  return null
}

function partyLabel(code: string | null | undefined, name: string | null | undefined) {
  return partyName(code, name) || 'Unknown party'
}

function isVirtualFlag(value: unknown) {
  return value === true || value === 1 || value === '1'
}

function stagePlace(stage: LifecycleStage) {
  return (
    [stage.warehouse, stage.customerLocation].filter(Boolean).join(' · ') ||
    partyLabel(stage.receiverCode, stage.receiverName)
  )
}

function buildNarrative(stages: LifecycleStage[], tagCode: string) {
  if (stages.length === 0) {
    return `No scannable movement story found for ${tagCode} yet.`
  }
  const first = stages[0]
  const last = stages[stages.length - 1]
  const docs = new Set(stages.map((s) => s.document).filter(Boolean))
  const places = new Set(stages.map(stagePlace))
  return `Tag ${tagCode} began with ${first.transferType} on ${formatDateTime(first.createdAt)}, travelling through ${docs.size} document${docs.size === 1 ? '' : 's'} and ${places.size} place${places.size === 1 ? '' : 's'}. It is currently at ${stagePlace(last)} after ${last.transferType}.`
}

export default function TagJourneyPage() {
  const regionView = useRegionStore((s) => s.view)
  const [params, setParams] = useSearchParams()
  const code = params.get('code') || ''
  const [input, setInput] = useState(code)
  const [showRaw, setShowRaw] = useState(false)

  const q = useQuery({
    queryKey: ['tag-journey-story', regionView, code],
    queryFn: () => dashboardService.getLifecycle(code),
    enabled: Boolean(code),
  })

  const load = (value: string) => {
    const next = value.trim()
    setInput(next)
    setShowRaw(false)
    setParams(next ? { code: next } : {})
  }

  const story = useMemo(() => {
    if (!q.data) return null
    const history = (q.data.history ?? []).filter((h) => !isVirtualFlag(h.is_virtual))
    const stages = (q.data.stages ?? []).filter((s) => !isVirtualFlag(s.isVirtual))
    const currentIsVirtual = q.data.current ? isVirtualFlag(q.data.current.is_virtual) : false
    return {
      history,
      stages,
      current: currentIsVirtual ? null : q.data.current,
      uniqueTransferTypes: q.data.uniqueTransferTypes.filter((t) =>
        stages.some((s) => s.transferType === t)
      ),
      allVirtual:
        (q.data.history?.length ?? 0) > 0 &&
        history.length === 0 &&
        (q.data.current == null || currentIsVirtual),
    }
  }, [q.data])

  const typeChart = useMemo(() => {
    if (!story) return []
    const counts = new Map<string, number>()
    for (const s of story.stages) {
      counts.set(s.transferType, (counts.get(s.transferType) ?? 0) + 1)
    }
    return [...counts.entries()].map(([name, count]) => ({ name, count }))
  }, [story])

  const pathChart = useMemo(() => {
    if (!story) return []
    return story.stages.map((s, i) => ({
      step: i + 1,
      label: `Hop ${i + 1}`,
      place: stagePlace(s).slice(0, 28),
      type: s.transferType,
    }))
  }, [story])

  return (
    <PageContainer fluid className="tag-story-page">
      <PageHeader
        title="Tag Journey"
        subtitle="Story-style path for one scannable RFID tag across documents, parties, and places."
      />

      <SectionCard
        title="Lookup"
        subtitle="Scannable RFID tags only — non-scan line items are excluded from the story."
      >
        <LookupBar
          type="tag"
          value={input}
          placeholder="Search RFID tag code…"
          submitLabel="Open journey"
          onChange={setInput}
          onSubmit={load}
        />
      </SectionCard>

      {!code ? (
        <PageEmpty
          icon={<Radio size={28} className="text-primary" />}
          description="Search a real RFID code to see where it started, which documents carried it, and how it moved from sender to receiver."
        />
      ) : q.isLoading ? (
        <PageLoading />
      ) : q.isError ? (
        <PageError message={(q.error as { message?: string })?.message} />
      ) : story?.allVirtual ? (
        <Alert
          type="warning"
          showIcon
          message="This code looks like a non-scannable line item"
          description="Tag Journey visualises actual RFID tags only. Try Movements or Current State with the non-scannable filter for NON_SCAN rows."
        />
      ) : !story || story.stages.length === 0 ? (
        <PageEmpty description="No scannable movement history for this tag" />
      ) : (
        <>
            <section className="tag-story-hero">
              <div className="tag-story-hero__eyebrow">
                <Radio size={14} />
                Scannable RFID journey
              </div>
              <h2 className="tag-story-hero__code">{code}</h2>
              <p className="tag-story-hero__narrative">{buildNarrative(story.stages, code)}</p>
              <div className="tag-story-hero__stats">
                <div>
                  <span className="tag-story-hero__stat-value">{story.stages.length}</span>
                  <span className="tag-story-hero__stat-label">Hops</span>
                </div>
                <div>
                  <span className="tag-story-hero__stat-value">
                    {new Set(story.stages.map((s) => s.document)).size}
                  </span>
                  <span className="tag-story-hero__stat-label">Documents</span>
                </div>
                <div>
                  <span className="tag-story-hero__stat-value">
                    {story.uniqueTransferTypes.length}
                  </span>
                  <span className="tag-story-hero__stat-label">Transfer types</span>
                </div>
                <div>
                  <span className="tag-story-hero__stat-value">
                    {new Set(story.stages.map(stagePlace)).size}
                  </span>
                  <span className="tag-story-hero__stat-label">Places</span>
                </div>
              </div>
              {story.current ? (
                <div className="tag-story-now">
                  <MapPin size={16} />
                  <div>
                    <div className="tag-story-now__label">Now</div>
                    <div className="tag-story-now__value">
                      {partyLabel(story.current.sender_code, story.current.sender_name)}
                      <ArrowRight size={14} className="mx-1 inline opacity-60" />
                      {partyLabel(story.current.receiver_code, story.current.receiver_name)}
                      {story.current.transfer_type ? (
                        <span className="tag-story-now__type"> · {story.current.transfer_type}</span>
                      ) : null}
                    </div>
                    {story.current.last_document ? (
                      <Link
                        className="tag-story-now__doc"
                        to={`/movement-document?document=${encodeURIComponent(story.current.last_document)}`}
                      >
                        Latest document {story.current.last_document}
                      </Link>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </section>

            <SectionCard
              title="Journey path"
              subtitle="Each point is a hop in the tag’s story — scroll sideways on smaller screens."
            >
              <div className="tag-story-path" role="list">
                {story.stages.map((stage, index) => (
                  <article key={`${stage.document}-${stage.createdAt}-${index}`} className="tag-story-beat" role="listitem">
                    <div className="tag-story-beat__index">{index + 1}</div>
                    <div className="tag-story-beat__type">{stage.transferType}</div>
                    <div className="tag-story-beat__when">{formatDateTime(stage.createdAt)}</div>
                    <div className="tag-story-beat__flow">
                      <span>{partyLabel(stage.senderCode, stage.senderName)}</span>
                      <ArrowRight size={12} />
                      <span>{partyLabel(stage.receiverCode, stage.receiverName)}</span>
                    </div>
                    <div className="tag-story-beat__place">
                      <MapPin size={12} />
                      {stagePlace(stage)}
                    </div>
                    <Link
                      className="tag-story-beat__doc"
                      to={`/movement-document?document=${encodeURIComponent(stage.document)}`}
                    >
                      Doc {stage.document}
                    </Link>
                    {index < story.stages.length - 1 ? (
                      <div className="tag-story-beat__connector" aria-hidden />
                    ) : null}
                  </article>
                ))}
              </div>
            </SectionCard>

            <div className="tag-story-charts">
              <SectionCard title="Movement mix" subtitle="How often each transfer type appears">
                {typeChart.length === 0 ? (
                  <Empty />
                ) : (
                  <div className="tag-story-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={typeChart} margin={{ top: 8, right: 8, left: 0, bottom: 28 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis
                          dataKey="name"
                          tick={{ fontSize: 10 }}
                          interval={0}
                          angle={-20}
                          textAnchor="end"
                          height={48}
                        />
                        <YAxis allowDecimals={false} width={32} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                          {typeChart.map((_, i) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </SectionCard>

              <SectionCard title="Path over time" subtitle="Hop sequence through the journey">
                <div className="tag-story-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={pathChart} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis
                        dataKey="step"
                        allowDecimals={false}
                        width={28}
                        tick={{ fontSize: 11 }}
                        domain={[1, 'dataMax']}
                      />
                      <Tooltip
                        formatter={(_v, _n, item) => [
                          (item?.payload as { place?: string; type?: string })?.place ?? '',
                          (item?.payload as { type?: string })?.type ?? 'Place',
                        ]}
                      />
                      <Line
                        type="monotone"
                        dataKey="step"
                        stroke="#af0000"
                        strokeWidth={2.5}
                        dot={{ r: 5, fill: '#af0000', strokeWidth: 0 }}
                        activeDot={{ r: 7 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </SectionCard>
            </div>

            <SectionCard
              title="Story chapters"
              subtitle="Lifecycle stages in first-appearance order of transfer types"
            >
              <div className="tag-story-chapters">
                {story.uniqueTransferTypes.map((type, chapterIndex) => {
                  const hops = story.stages.filter((s) => s.transferType === type)
                  const first = hops[0]
                  if (!first) return null
                  return (
                    <article key={type} className="tag-story-chapter">
                      <div className="tag-story-chapter__rail">
                        <span className="tag-story-chapter__dot" />
                        {chapterIndex < story.uniqueTransferTypes.length - 1 ? (
                          <span className="tag-story-chapter__line" />
                        ) : null}
                      </div>
                      <div className="tag-story-chapter__body">
                        <div className="tag-story-chapter__title">
                          <Route size={14} />
                          Chapter {chapterIndex + 1}: {type}
                        </div>
                        <Text type="secondary" className="text-xs">
                          First seen {formatDateTime(first.createdAt)} · {hops.length} hop
                          {hops.length === 1 ? '' : 's'}
                        </Text>
                        <ul className="tag-story-chapter__hops">
                          {hops.map((h, i) => (
                            <li key={`${h.document}-${h.createdAt}-${i}`}>
                              <strong>{formatDateTime(h.createdAt)}</strong>
                              {' — '}
                              {partyLabel(h.senderCode, h.senderName)} →{' '}
                              {partyLabel(h.receiverCode, h.receiverName)}
                              {' · '}
                              <Link
                                to={`/movement-document?document=${encodeURIComponent(h.document)}`}
                              >
                                {h.document}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </article>
                  )
                })}
              </div>
            </SectionCard>

            <SectionCard
              title="Detailed history"
              subtitle="Optional tabular view of the same scannable hops"
              actions={
                <Button type="link" onClick={() => setShowRaw((v) => !v)}>
                  {showRaw ? 'Hide table' : 'Show table'}
                </Button>
              }
              variant={showRaw ? 'table' : 'default'}
            >
              {showRaw ? (
                <MovementTable rows={story.history as MovementRow[]} />
              ) : (
                <Text type="secondary">
                  The story above is the primary view. Open the table if you need exact columns and
                  sorting.
                </Text>
              )}
            </SectionCard>
          </>
      )}
    </PageContainer>
  )
}
